import { DateHelper } from '@connect-soft/date-helper';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import { DatePicker, IDatePickerProps } from '../DatePicker';

export type IValueType<T> = {
    start: T;
    end: T;
};

export type IDateRangePickerProps = Omit<
    IDatePickerProps,
    'minDate' | 'maxDate' | 'error' | 'helperText' | 'onError' | 'onChange' | 'value'
> & {
    label?: string | IValueType<string>;
    value?: IValueType<IDatePickerProps['value']>;
    hidden?: boolean;
    minDate?: IDatePickerProps['value'];
    maxDate?: IDatePickerProps['value'];
    optionalEnd?: boolean;
    error?: boolean | IValueType<boolean>;
    helperText?: string | IValueType<string>;
    onChange?: (value: IValueType<IDatePickerProps['value']>) => void;
};

const StyledWrapper = styled(Box)`
    display: flex;
    justify-content: space-between;
`;
const StyledDatePicker = styled(DatePicker)`
    flex: 1;
`;
const StyledSign = styled(Box, {
    shouldForwardProp: (propName) => 'size' !== propName
})<{
    size: IDateRangePickerProps['size'];
}>(({ theme, size }) => ({
    margin: `${theme.spacing(1)} ${theme.spacing(1.7)} 0`,
    ...(size === 'small' && { fontSize: '1rem' })
}));

export const DateRangePicker = ({
    error,
    helperText,
    label,
    minDate,
    maxDate,
    name,
    optionalEnd = false,
    required,
    value,
    onChange,
    ...rest
}: IDateRangePickerProps) => {
    const validValue = useMemo(() => ({ start: null, end: null, ...value }), [value]);

    return (
        <>
            {label && <FormLabel required={required}>{label}</FormLabel>}
            <StyledWrapper>
                <StyledDatePicker
                    {...rest}
                    name={`${name}.start`}
                    value={validValue.start}
                    minDate={minDate ?? undefined}
                    maxDate={validValue.end ?? maxDate ?? undefined}
                    error={typeof error === 'boolean' ? error : !!error?.start}
                    helperText={typeof helperText === 'object' ? helperText?.start : undefined}
                    onChange={
                        onChange
                            ? (newValue) =>
                                  onChange({
                                      ...validValue,
                                      start: DateHelper.from(newValue).getStartOfDay().getObj() ?? null
                                  })
                            : undefined
                    }
                />
                <StyledSign size={rest.size}> - </StyledSign>
                <StyledDatePicker
                    {...rest}
                    name={`${name}.end`}
                    value={validValue.end}
                    minDate={validValue.start ?? minDate ?? undefined}
                    maxDate={maxDate ?? undefined}
                    error={typeof error === 'boolean' ? error : !!error?.end}
                    required={!optionalEnd && required}
                    helperText={typeof helperText === 'object' ? helperText?.end : undefined}
                    onChange={
                        onChange
                            ? (newValue) =>
                                  onChange({
                                      ...validValue,
                                      end: DateHelper.from(newValue).getStartOfDay().getObj() ?? null
                                  })
                            : undefined
                    }
                />
            </StyledWrapper>
            {typeof helperText === 'string' && (
                <FormHelperText error={typeof error === 'boolean' && error}>{helperText}</FormHelperText>
            )}
        </>
    );
};
